import React, { useState, useEffect, useMemo } from "react";
import Select from "../../Select";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  Line,
} from "recharts";

const yAxisLabelNitrogen = "MT N";
const yAxisDxNitrogen = -yAxisLabelNitrogen.length;

function getSelectValue(value) {
  if (value && typeof value === "object" && "value" in value) {
    return value.value;
  }
  return value ?? "";
}

function pickSeries(block) {
  if (Array.isArray(block)) return block;
  if (!block || typeof block !== "object") return [];

  const preferred = ["Global", "Average of all countries"];
  for (const key of preferred) {
    if (Array.isArray(block[key])) return block[key];
  }

  const directArray = Object.values(block).find((value) => Array.isArray(value));
  return directArray || [];
}

function NitrogenLegend({ payload, hiddenKeys, onToggle }) {
  if (!payload) return null;

  return (
    <div className="mb-2 flex w-full flex-wrap items-center justify-center gap-4 text-[12px] text-[#98999A]">
      {payload.map((entry) => {
        const isHidden = hiddenKeys.includes(entry.dataKey);
        const key = entry.dataKey || entry.value;
        const isTarget = entry.dataKey === "target" || entry.value === "Target";
        const color = isTarget ? "#FF0000" : entry.color || "#999";

        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(entry)}
            className={`flex items-center gap-1.5 ${isHidden ? "opacity-40" : ""}`}
          >
            <span
              className={`h-3 w-3 ${isTarget ? "rounded-full" : "rounded-[2px]"}`}
              style={{ backgroundColor: color }}
            />
            <span>{entry.value || entry.dataKey}</span>
          </button>
        );
      })}
    </div>
  );
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || payload.length === 0) return null;

  const sortedPayload = [...payload].sort((a, b) => {
    const av = typeof a.value === "number" ? a.value : 0;
    const bv = typeof b.value === "number" ? b.value : 0;
    return bv - av;
  });

  return (
    <div className="rounded-lg border border-[#EDF0F2] bg-white px-3 py-2 text-[11px] shadow-md">
      <div className="mb-1 text-[11px] font-medium text-[#323334]">{label}</div>
      {sortedPayload.map((item) => (
        <div
          key={item.dataKey}
          className="mb-0.5 flex items-center justify-between gap-2"
        >
          <div className="flex items-center gap-1.5">
            <span
              className="h-2 w-2 rounded-[2px]"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-[#7E8081]">{item.name}</span>
          </div>
          <span className="font-medium text-[#323334]">
            {typeof item.value === "number"
              ? item.value.toLocaleString("en-US", {
                  maximumFractionDigits: 0,
                })
              : item.value}
            <span className="text-[#98999A]"> {yAxisLabelNitrogen}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

export default function NitrogenTab({ data }) {
  const countries = useMemo(() => {
    if (!data || typeof data !== "object") return [];
    if (Array.isArray(data.countries)) return data.countries;
    return [];
  }, [data]);

  const defaultCountry = useMemo(() => {
    if (countries.includes("Global")) return "Global";
    return countries[0] || "";
  }, [countries]);

  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const handleLegendClick = (entry) => {
    const { dataKey } = entry || {};
    if (!dataKey) return;

    setHiddenKeys((prev) =>
      prev.includes(dataKey)
        ? prev.filter((key) => key !== dataKey)
        : [...prev, dataKey],
    );
  };

  useEffect(() => {
    if (!selectedCountry && defaultCountry) {
      setSelectedCountry(defaultCountry);
    } else if (selectedCountry && countries.length > 0 && !countries.includes(selectedCountry)) {
      setSelectedCountry(defaultCountry);
    }
  }, [selectedCountry, defaultCountry, countries]);

  const chartData = useMemo(() => {
    const block = data?.["Nitrogen Application"];
    const series = pickSeries(block);
    return Array.isArray(series) ? series.map((row) => ({
      ...row,
      year: Number(row.year),
      calcN_org: Number(row.calcN_org) || 0,
      calcN_synth: Number(row.calcN_synth) || 0,
    })) : [];
  }, [data]);

  const selectedSeries = useMemo(() => {
    if (!selectedCountry || !data) return [];
    const block = data?.["Nitrogen Application"];
    const countrySeries = Array.isArray(block?.[selectedCountry]) ? block[selectedCountry] : [];
    return Array.isArray(countrySeries) ? countrySeries.map((row) => ({
      ...row,
      year: Number(row.year),
      calcN_org: Number(row.calcN_org) || 0,
      calcN_synth: Number(row.calcN_synth) || 0,
      target: Number(row.year) === 2050 ? 68000 : null,
    })) : [];
  }, [data, selectedCountry]);

  const countryOptions = useMemo(
    () => countries.map((countryName) => ({ value: countryName, label: countryName })),
    [countries],
  );

  return (
    <div className="flex w-full flex-col gap-10">
      <section className="flex w-full flex-col gap-4">
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <h2 className="text-[18px] font-medium text-[#323334]">Nitrogen Application</h2>
          <div className="w-full max-w-sm">
            <Select
              label="Country"
              value={selectedCountry}
              onChange={(value) => setSelectedCountry(getSelectValue(value))}
              options={countryOptions}
              placeholder="Select country"
              disabled={countryOptions.length === 0}
              className="w-full"
            />
          </div>
        </div>

        <div className="h-[600px] rounded-xl border border-[#EDF0F2] px-6 py-4">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={Array.isArray(selectedSeries) ? selectedSeries : []}
              stackOffset="sign"
              margin={{ top: 40, right: 30, left: 60, bottom: 30 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="year"
                interval={0}
                angle={-45}
                textAnchor="end"
                height={70}
                tick={{ fill: "#98999A", fontSize: 12, fontWeight: 700 }}
                tickMargin={10}
              >
                <Label
                  value="Years"
                  position="insideBottom"
                  offset={-20}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </XAxis>
              <YAxis
                width={60}
                tick={{ fill: "#98999A", fontSize: 12 }}
                tickFormatter={(value) =>
                  Number(value).toLocaleString("en-US", {
                    maximumFractionDigits: 0,
                  })
                }
              >
                <Label
                  value={yAxisLabelNitrogen}
                  angle={-90}
                  position="left"
                  dx={yAxisDxNitrogen}
                  style={{ fill: "#98999A", fontSize: 15 }}
                />
              </YAxis>
              <Tooltip content={(props) => <CustomTooltip {...props} />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
              <Legend
                verticalAlign="top"
                align="center"
                wrapperStyle={{ top: 0, left: 0, width: "100%", justifyContent: "center" }}
                content={(props) => (
                  <NitrogenLegend
                    {...props}
                    hiddenKeys={hiddenKeys}
                    onToggle={handleLegendClick}
                  />
                )}
              />
              <Bar dataKey="calcN_org" stackId="nitrogen" fill="#72BCE3" name="Organic N" hide={hiddenKeys.includes("calcN_org")} />
              <Bar dataKey="calcN_synth" stackId="nitrogen" fill="#FF5579" name="Synthetic N" hide={hiddenKeys.includes("calcN_synth")} />
              {selectedCountry === "Global" && (
                <Line
                  type="monotone"
                  dataKey="target"
                  name="Target"
                  stroke="transparent"
                  dot={{ stroke: "#FF0000", fill: "#FF0000", r: 6 }}
                  activeDot={false}
                  isAnimationActive={false}
                  hide={hiddenKeys.includes("target")}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </div>
  );
}
